import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, Phone, Trash2, Loader2, Calendar, Check, X, Tag, User, MessageSquare } from 'lucide-react';

const ManageInterests = () => {
    const [interests, setInterests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        fetchInterests();
    }, []);

    const fetchInterests = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/viewinterests`, {
                headers: { authorization: `Bearer ${token}` } 
            });
            setInterests(res.data.intresteds || res.data.interested || []);
        } catch (err) {
            console.error("Fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Remove this interest lead?")) return;
        setDeletingId(id);
        try {
            const token = localStorage.getItem('adminToken');
            await axios.delete(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/deleteinterest/${id}`, {
                headers: { authorization: `Bearer ${token}` }
            });
            setInterests(interests.filter(item => item._id !== id));
        } catch (err) {
            console.error("Delete error:", err);
            alert("Failed to remove lead");
        } finally {
            setDeletingId(null);
        }
    };

    if (loading) return (
        <div className="h-96 flex flex-col items-center justify-center">
            <Loader2 className="animate-spin text-orange-500 mb-4" size={40} />
            <p className="text-slate-400 font-bold tracking-widest uppercase text-[10px]">Syncing Leads...</p>
        </div>
    );

    return (
        <div className="p-4 md:p-8">
            <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-black uppercase italic leading-none">
                    Product <span className="text-orange-500">Interests</span>
                </h1>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-2">
                    {interests.length} Potential Leads
                </p>
            </div>

            {/* --- DESKTOP TABLE --- */}
            <div className="hidden lg:block bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-slate-900 text-white text-[10px] uppercase tracking-[0.2em]">
                        <tr>
                            <th className="p-5 text-center">SL</th>
                            <th className="p-5">Date</th>
                            <th className="p-5">Category / Product</th>
                            <th className="p-5">Customer Details</th>
                            <th className="p-5 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm font-medium text-slate-700 divide-y divide-slate-50">
                        {interests.map((item, index) => (
                            <tr key={item._id} className="hover:bg-orange-50/20 transition-all group">
                                <td className="p-5 text-slate-300 font-black italic text-center">
                                    {String(index + 1).padStart(2, '0')}
                                </td>
                                <td className="p-5">
                                    <div className="flex items-center gap-2 text-slate-500 text-xs font-bold">
                                        <Calendar size={14} className="text-orange-500" />
                                        {new Date(item.createdAt).toLocaleDateString('en-GB')}
                                    </div>
                                </td>
                                <td className="p-5">
                                    <div className="flex flex-col">
                                        <span className="text-orange-600 font-black uppercase text-[10px] tracking-wider mb-0.5">
                                            {item.productId?.categoryId?.categoryName || "Fleet"}
                                        </span>
                                        <span className="font-black text-slate-900 uppercase italic tracking-tight">
                                            {item.productId?.productName || "Product Deleted"}
                                        </span>
                                    </div>
                                </td>
                                <td className="p-5">
                                    <div className="flex flex-col gap-1">
                                        <span className="font-black text-slate-900 uppercase text-xs tracking-tight">{item.customerName}</span>
                                        <div className="flex items-center gap-3">
                                            <div className="flex items-center gap-1 text-slate-400 text-[11px]"><Mail size={12} /> {item.customerEmail}</div>
                                            <div className="flex items-center gap-1 text-slate-600 font-bold text-[11px]"><Phone size={12} className="text-orange-500" /> {item.customerPhone}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-5">
                                    <div className="flex items-center justify-center gap-2">
                                        <button className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center hover:bg-emerald-600 hover:text-white transition-all shadow-sm">
                                            <Check size={18} strokeWidth={3} />
                                        </button>
                                        <button 
                                            disabled={deletingId === item._id}
                                            onClick={() => handleDelete(item._id)}
                                            className="w-9 h-9 rounded-xl bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-500 hover:text-white transition-all shadow-sm disabled:opacity-50"
                                        >
                                            {deletingId === item._id ? <Loader2 size={16} className="animate-spin" /> : <X size={18} strokeWidth={3} />}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* --- MOBILE CARD VIEW --- */}
            <div className="lg:hidden space-y-4">
                {interests.map((item, index) => (
                    <div key={item._id} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm relative">
                        <div className="flex justify-between items-start mb-4">
                            <div className="flex items-center gap-2 bg-orange-100 px-2 py-1 rounded text-orange-600">
                                <Tag size={12} />
                                <span className="text-[9px] font-black uppercase tracking-widest">
                                    {item.productId?.categoryId?.categoryName || "Lead"}
                                </span>
                            </div>
                            <span className="text-[10px] font-bold text-slate-400 italic">
                                {new Date(item.createdAt).toLocaleDateString('en-GB')}
                            </span>
                        </div>

                        <h3 className="text-sm font-black text-slate-900 uppercase italic mb-4 tracking-tight">
                            {item.productId?.productName || "Product Reference Unavailable"}
                        </h3>

                        <div className="space-y-3 bg-slate-50 p-4 rounded-xl mb-5">
                            <div className="flex items-center gap-3 text-slate-900">
                                <User size={14} className="text-slate-400" />
                                <span className="text-xs font-black uppercase tracking-tight">{item.customerName}</span>
                            </div>
                            <div className="flex items-center gap-3 text-slate-600">
                                <Phone size={14} className="text-orange-500" />
                                <span className="text-xs font-bold">{item.customerPhone}</span>
                            </div>
                            <div className="flex items-center gap-3 text-slate-600">
                                <Mail size={14} className="text-slate-400" />
                                <span className="text-xs font-medium truncate">{item.customerEmail}</span>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <button className="flex-1 flex items-center justify-center gap-2 bg-emerald-50 text-emerald-600 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-active active:scale-95">
                                <Check size={14} strokeWidth={3} /> Verified
                            </button>
                            <button 
                                onClick={() => handleDelete(item._id)}
                                disabled={deletingId === item._id}
                                className="flex-1 flex items-center justify-center gap-2 bg-red-50 text-red-500 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-active active:scale-95 disabled:opacity-50"
                            >
                                {deletingId === item._id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Remove
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {interests.length === 0 && (
                <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-slate-100">
                    <MessageSquare className="mx-auto text-slate-100 mb-4" size={64} />
                    <p className="text-slate-400 font-bold uppercase tracking-[0.2em] text-[10px]">Your interest list is empty</p>
                </div>
            )}
        </div>
    );
};

export default ManageInterests;